import { AuthProvider } from "@/components/context/AuthProvider"
import FeatureProvider from "@/components/context/FeatureProvider"
import GlobalContextProvider from "@/components/context/GlobalContextProvider"
import { getAuthToken } from "@/helpers/auth"
import { grantAnalyticsConsent } from "@/helpers/fullstory"
import { initGoogleTagManager } from "@/helpers/googleTagManager"
import { addMapStylesheets, loadCalendly, loadHubSpot } from "@/helpers/thirdPartyScripts"
import { whenPageIdle } from "@/helpers/whenPageIdle"
import { createApolloCache } from "@/lib/apolloCache"
import { ApolloClient, ApolloProvider, createHttpLink, split } from "@apollo/client"
import { setContext } from "@apollo/client/link/context"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { ConfigProvider } from "antd"
import en_GB from "antd/es/locale-provider/en_GB"
import moment from "moment"
import "moment/locale/en-gb"
import { FC, PropsWithChildren, useEffect } from "react"

import Pages from "../../pages"
import { ErrorBoundary } from "./ErrorBoundary"

moment.locale("en-gb", { week: { dow: 1 } })

// ===> initial settings just for backup the fetched settings from Strapi

type TooltipMessages = {
  temporaryUnavailable: string
  createApplicationBtn: string
}
export const TOOLTIP_MESSAGES: TooltipMessages = {
  temporaryUnavailable: "Temporary unavailable",
  createApplicationBtn: "To create and submit an application please start here.",
}

// ====================
// Apollo Client Factory
// ====================

function resolveApolloCredentials(): RequestCredentials {
  const apiUrl = import.meta.env.WEBSITE_API_URL ?? "/graphql"
  // No window at build time (src/entry-prerender.tsx imports this module); the prerender never sends a request.
  if (!apiUrl.startsWith("http") || typeof window === "undefined") {
    return "same-origin"
  }
  try {
    const apiOrigin = new URL(apiUrl).origin
    return apiOrigin === window.location.origin ? "same-origin" : "include"
  } catch {
    return "same-origin"
  }
}

const httpLink = createHttpLink({
  uri: `${import.meta.env.WEBSITE_API_URL ?? "/graphql"}`,
  credentials: resolveApolloCredentials(),
})

const authLink = setContext((_, { headers }) => {
  const token = getAuthToken()
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : undefined,
    },
  }
})

/** Strapi rejects invalid Bearer before `auth: false` runs — these must not use `authLink`. */
const GRAPHQL_OPERATIONS_WITHOUT_ACCESS_BEARER = new Set(["refreshAccessToken", "customLogout"])

const splitLink = split(
  ({ operationName }) => GRAPHQL_OPERATIONS_WITHOUT_ACCESS_BEARER.has(operationName ?? ""),
  httpLink,
  authLink.concat(httpLink)
)
// Patch console.warn to handle Apollo cache normalization errors gracefully
// This prevents "Cannot convert object to primitive value" errors when Apollo
// tries to log warnings about cache data loss during StoreWriter.writeToStore
const originalConsoleWarn = console.warn
console.warn = function (...args: any[]) {
  try {
    // Check if this is an Apollo cache warning (common pattern)
    const isApolloCacheWarning = args.some(arg => {
      try {
        return (
          typeof arg === "string" &&
          (arg.includes("data loss") || arg.includes("StoreWriter") || arg.includes("writeToStore"))
        )
      } catch {
        return false
      }
    })

    if (isApolloCacheWarning) {
      // For Apollo cache warnings, safely stringify all arguments
      const safeArgs: any[] = []
      for (const arg of args) {
        try {
          if (arg === null || arg === undefined) {
            safeArgs.push(String(arg))
          } else if (typeof arg === "string" || typeof arg === "number" || typeof arg === "boolean") {
            safeArgs.push(arg)
          } else if (typeof arg === "object") {
            try {
              // Try JSON.stringify first
              const stringified = JSON.stringify(
                arg,
                (key, value) => {
                  // Skip functions and symbols
                  if (typeof value === "function" || typeof value === "symbol") {
                    return "[Function]"
                  }
                  // Handle circular references
                  if (value === arg && key !== "") {
                    return "[Circular]"
                  }
                  return value
                },
                2
              )
              safeArgs.push(stringified)
            } catch (jsonError) {
              // If JSON.stringify fails, try a safer approach
              try {
                // Try to get a basic description
                const objType = Object.prototype.toString.call(arg)
                safeArgs.push(`[${objType}]`)
              } catch {
                // Last resort: just indicate it's an object
                safeArgs.push("[Object]")
              }
            }
          } else {
            // For other types (functions, symbols, etc.), just skip or use placeholder
            safeArgs.push(`[${typeof arg}]`)
          }
        } catch (argError) {
          // If we can't process this argument at all, skip it
          safeArgs.push("[Unserializable]")
        }
      }
      originalConsoleWarn.apply(console, safeArgs)
    } else {
      // For other warnings, try to use original behavior but with safety
      try {
        originalConsoleWarn.apply(console, args)
      } catch {
        // If even the original fails, log a safe message
        originalConsoleWarn("[Warning: original console.warn failed]")
      }
    }
  } catch (error) {
    // If even the safe conversion fails, just log a generic message
    // Don't try to include the error object as it might also fail to convert
    try {
      originalConsoleWarn("[Warning suppressed due to serialization error]")
    } catch {
      // If even this fails, do nothing (silent fail)
    }
  }
}

const client = new ApolloClient({
  link: splitLink,
  // Client identification for Apollo Server metrics and debugging
  name: "easetech-web-client",
  version: (import.meta.env as any).APP_VERSION || "1.0.0",
  // Enable Apollo DevTools only in development
  connectToDevTools: import.meta.env.DEV,
  // Prevent duplicate queries from being sent simultaneously
  queryDeduplication: true,
  // Assume results are immutable for better performance (recommended for production)
  assumeImmutableResults: true,
  cache: createApolloCache(),
  // Global default options for all queries, mutations, and watch queries
  defaultOptions: {
    watchQuery: {
      // Return both data and errors so UI can handle partial failures gracefully
      errorPolicy: "all",
      // Show cached data immediately, fetch fresh data in background
      fetchPolicy: "cache-and-network",
      // Notify subscribers when network errors occur
      notifyOnNetworkStatusChange: false,
    },
    query: {
      // Return both data and errors
      errorPolicy: "all",
      // Check cache first, then fetch from network if not found
      fetchPolicy: "cache-first",
    },
    mutate: {
      // Return both data and errors for partial mutation success handling
      errorPolicy: "all",
      // Always fetch from network for mutations to ensure data consistency
      fetchPolicy: "network-only",
    },
  },
})
const queryClient = new QueryClient()

type AppProvidersProps = PropsWithChildren<{ apolloClient: ApolloClient<object>; queryClient: QueryClient }>

/** Everything around the router. Shared with the build-time prerender, so both render the same tree. */
export const AppProviders: FC<AppProvidersProps> = ({ apolloClient, queryClient, children }) => (
  <ErrorBoundary>
    <ApolloProvider client={apolloClient}>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <FeatureProvider>
            <GlobalContextProvider>
              <ConfigProvider locale={en_GB}>{children}</ConfigProvider>
            </GlobalContextProvider>
          </FeatureProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ApolloProvider>
  </ErrorBoundary>
)

const App: FC = () => {
  useEffect(() => {
    addMapStylesheets()
    // Analytics and marketing scripts wait until the page has loaded and gone idle, so they don't slow its first
    // render on phones (FullStory alone took ~4 s of main thread in Lighthouse). Events pushed to the dataLayer before
    // GTM loads are kept and sent when it does.
    return whenPageIdle(() => {
      // Temporary behavior until consent banner is implemented:
      // auto-grant analytics so FullStory can record landing/auth flows too.
      grantAnalyticsConsent()
      initGoogleTagManager()
      loadHubSpot().catch(() => console.info("[analytics] HubSpot script blocked or failed to load"))
      loadCalendly().catch(() => console.info("[analytics] Calendly script blocked or failed to load"))
    })
  }, [])

  return (
    <AppProviders apolloClient={client} queryClient={queryClient}>
      <Pages />
    </AppProviders>
  )
}

export { App }
